import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { VehiclePartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehiclePartCategoryTreeItem';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { DocumentationAlertMotrioRequest } from '@1po/1po-bff-fe-spec/generated/email/request/SendDocumentationAlertMotrio';
import { OrderReferenceItem } from '@1po/1po-bff-fe-spec/generated/order/response/GetDealerOrderPageResponse';
import { Form as AntForm } from 'antd';
import { TFunction } from 'i18next';
import { useDebouncedCallback } from 'use-debounce';
import { RootState } from 'app/AppStore';
import { SItem } from 'components/DocumentationAlertButton/DocumentaryAlertForm.styled';
import { useFillDocumentationAlertState } from 'components/DocumentationAlertButton/useFillDocumentationAlertState';
import {
  getExplodedIAMTree,
  getExplodedIAMTreeItem,
  getExplodedTree,
  getExplodedTreeItem,
  getLastSearchedVehicleKey,
} from 'domains/catalog/Catalog.store';
import { UniversalProductReference } from 'domains/catalog/Catalog.types';
import { useIsDHCatalogSourceInUrl, useIsSearchInUrl } from 'domains/catalog/Catalog.utils';
import {
  sendEmailDocumentationAlertMotrioRequestSaga,
  setEmailDocumentationAlertMotrioData,
} from 'domains/email/Email.store';
import { SendDocumentationAlertMotrioLocal } from 'domains/email/Email.types';
import { getUserGarageInfo } from 'domains/garage/Garage.store';
import { DHReferenceLocal, IAMReferenceLocal } from 'domains/references';
import { TireReference } from 'domains/tires/Tire.types';
import { Checkbox, Flex, Form, Input, MarginBox, Select, Text, TextArea, WhiteButton, YellowButton } from 'UI';
import { getData } from 'utils';
import { formatVehicleBrand } from 'utils/mappers';

interface SelectRequestOption {
  title: string;
  value: DocumentationAlertMotrioRequest;
}

const requestOptions = (t: TFunction): SelectRequestOption[] => {
  return [
    {
      title: t(
        'motrio.documentation_alert.select.not_compatible_with_vehicle',
        'Reference not compatible with the vehicle',
      ),
      value: 'NOT_COMPATIBLE_WITH_VEHICLE',
    },
    {
      title: t('motrio.documentation_alert.select.wrong_content', 'Wrong reference/part content'),
      value: 'WRONG_CONTENT',
    },
    {
      title: t('motrio.documentation_alert.select.wrong_label', 'Wrong reference/part label'),
      value: 'WRONG_LABEL',
    },
    {
      title: t(
        'motrio.documentation_alert.select.incorrect_technical_characteristic',
        'Incorrect/missing technical characteristic',
      ),
      value: 'INCORRECT_TECHNICAL_CHARACTERISTIC',
    },
    {
      title: t(
        'motrio.documentation_alert.select.missing_technical_documentation',
        'Missing technical documentation (labour time, repair method, ...)',
      ),
      value: 'MISSING_TECHNICAL_DOCUMENTATION',
    },
    {
      title: t('motrio.documentation_alert.select.uncompetitive_pricing', 'Uncompetitive pricing'),
      value: 'UNCOMPETITIVE_PRICING',
    },
    {
      title: t('motrio.documentation_alert.select.unavailable_part', 'Unavailable part'),
      value: 'UNAVAILABLE_PART',
    },
  ];
};

interface FormItemProps {
  name: string;
  label?: string;
  type?: 'string' | 'email' | 'boolean' | any;
  required?: boolean;
  message?: string;
  children?: React.ReactNode;
  width?: string;
}

const FormItem = ({ name, label, type, required = false, message, children, width = '100%' }: FormItemProps) => (
  <SItem
    name={[name]}
    label={
      label ? (
        <Text disableGutter type="base" bold>
          {label}
        </Text>
      ) : undefined
    }
    rules={[{ required, type, message }]}
    width={width}
  >
    {children}
  </SItem>
);

export const isFilled = (s?: string): boolean => (s?.length ?? 0) > 0;

const sendDisable = (email: SendDocumentationAlertMotrioLocal): boolean => {
  if (!email) {
    return false;
  }
  return !(
    isFilled(email.email) &&
    isFilled(email.socialReason) &&
    isFilled(email.companyRegistrationNumber) &&
    isFilled(email.subject) &&
    isFilled(email.message)
  );
};

export function DocumentationAlertForm({
  onCancel,
  reference,
  vehicleRegistrationNumber,
  vehicleDetail,
}: {
  readonly onCancel: () => void;
  readonly reference:
    | DHReferenceLocal
    | IAMReferenceLocal
    | OrderReferenceItem
    | TireReference
    | UniversalProductReference
    | undefined;
  readonly vehicleRegistrationNumber: string | undefined;
  readonly vehicleDetail: VehicleDetail | undefined;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const garageInfo = useSelector(getUserGarageInfo);
  const vinRegex = /[a-zA-Z0-9]{9}[a-zA-Z0-9-]{2}\d{6}/g;
  const isDH = useIsDHCatalogSourceInUrl();
  const isSearchInUrl = useIsSearchInUrl();
  const lastSearchedVehicleKey = useSelector(getLastSearchedVehicleKey);
  const treeIAM = getData(useSelector((state: RootState) => getExplodedIAMTree(state, lastSearchedVehicleKey)));
  const treeDH = getData(useSelector((state: RootState) => getExplodedTree(state, lastSearchedVehicleKey)));

  const emailDataFromState = useFillDocumentationAlertState(
    getData(garageInfo),
    reference,
    vehicleRegistrationNumber,
    vehicleDetail,
  );

  const emailData = {
    ...emailDataFromState,
    vehicleRegistrationNumber: isSearchInUrl ? '' : emailDataFromState.vehicleRegistrationNumber,
    request: emailDataFromState.request ?? undefined,
  };

  const [vehicleRegNumber, setVehicleRegNumber] = useState(emailData.vehicleRegistrationNumber);

  const [vehicleVinCode, setVehicleVinCode] = useState(emailData.vehicleVin);

  const [additionalCommentsMessage, setAdditionalCommentsMessage] = useState(emailData.additionalComments);

  const [isMessageUpdating, setIsMessageUpdating] = useState(false);

  const [form] = AntForm.useForm();

  const { carPartGroupId, categoryId, subcategoryId } = useParams<{
    carPartGroupId: string;
    categoryId: string;
    subcategoryId: string;
  }>();

  const getItem = (arr: string[]) =>
    isDH
      ? getExplodedTreeItem(treeDH?.data as VehiclePartCategoryTreeItem[], arr)
      : getExplodedIAMTreeItem(treeIAM?.data, arr);

  const carPartGroup = getItem([carPartGroupId]);
  const category = getItem([carPartGroupId, categoryId]);
  const subcategory = getItem([carPartGroupId, categoryId, subcategoryId]);
  let breadcrumbMessage = '';

  if (carPartGroup) {
    const carPartGrouplowerCase = carPartGroup?.label.toLowerCase();
    breadcrumbMessage = `Home / My vehicle / ${carPartGrouplowerCase
      ?.charAt(0)
      .toUpperCase()}${carPartGrouplowerCase?.slice(1)} / ${category?.label} / ${subcategory?.label}`;
  }

  const rootInputMessage = `${breadcrumbMessage.length > 0 ? `${breadcrumbMessage}\n\n` : ''}VRN: ${vehicleRegNumber}
VIN: ${isSearchInUrl ? '' : `${vehicleVinCode}`}
Vehicle details: ${
    isSearchInUrl
      ? ''
      : `${formatVehicleBrand(emailData.vehicleBrand)} ${emailData.vehicleModel} ${
          emailData.vehicleVersion ?? emailData.vehicleEngineCode
        }`
  }
Reference: ${emailData.referenceNumber} ${emailData.referenceDescription}
Brand: ${emailData.referenceBrand}
`;

  useEffect(() => {
    let inputMessage = rootInputMessage;
    if (emailData.request || vehicleRegNumber || vehicleVinCode) {
      setIsMessageUpdating(true);
      const selectedRequestOption = requestOptions(t).find((option) => option.value === emailData.request);
      if (emailData.request) {
        inputMessage += `Request: ${selectedRequestOption ? selectedRequestOption.title : emailData.request}`;
      }
      setTimeout(() => setIsMessageUpdating(false), 50);
    }

    dispatch(
      setEmailDocumentationAlertMotrioData({
        ...emailData,
        message: inputMessage,
        additionalComments: additionalCommentsMessage,
        breadcrumb: breadcrumbMessage,
      }),
    );

    // eslint-disable-next-line
  }, [vehicleRegNumber, vehicleVinCode, JSON.stringify(emailData.request)]);

  const setEmailData = useDebouncedCallback((emailData: SendDocumentationAlertMotrioLocal) => {
    dispatch(setEmailDocumentationAlertMotrioData(emailData));
  }, 250);

  const handleCancel = () => {
    onCancel();
    clearAdditionalComments();
  };

  const handleSend = () => {
    dispatch(sendEmailDocumentationAlertMotrioRequestSaga());
    handleCancel();
  };

  const clearAdditionalComments = () => {
    setAdditionalCommentsMessage('');
  };

  const validationMessages = {
    types: {
      email: t('common.email.validation', 'Please enter valid e-mail address.'),
    },
  };

  return (
    <Flex direction={'column'}>
      <Text type={'h4_paragraph_title'}>{t('motrio.documentation_alert', 'Documentation Alert')}</Text>
      <Text type={'h5_bold'}>{t('motrio.documentation_alert.report', 'Report a documentation error on a part')}</Text>
      <Text type="sm" color="grey55">
        {t('motrio.documentation_alert.required_fields', 'Fields marked with an asterisk are required')}
      </Text>
      <MarginBox mt={15} />
      <Form
        form={form}
        validationMessages={validationMessages}
        onFinish={handleSend}
        layout={'vertical'}
        initialValues={emailData}
      >
        <Flex direction={'column'}>
          <Flex direction={'row'}>
            <FormItem
              name={'socialReason'}
              label={t('assistance.documentation_alert.social_reason', 'Social reason/Commercial name')}
              type={'string'}
              required
            >
              <Input
                disabled
                onChange={(socialReason) => {
                  setEmailData({ ...emailData, socialReason });
                }}
                placeholder={t('assistance.documentation_alert.social_reason.placeholder', 'Your social reason')}
                bordered
              />
            </FormItem>
            <MarginBox ml={15} />
            <FormItem
              name={'companyRegistrationNumber'}
              label={t('assistance.documentation_alert.company_registration_number', 'Company registration number')}
              type={'string'}
              required
            >
              <Input
                disabled
                onChange={(companyRegistrationNumber) => setEmailData({ ...emailData, companyRegistrationNumber })}
                placeholder={t(
                  'assistance.documentation_alert.company_registration_number.placeholder',
                  'Your company registration number',
                )}
                bordered
              />
            </FormItem>
            <MarginBox ml={15} />
            <FormItem
              name={'vehicleRegistrationNumberOrVin'}
              label={t(
                'assistance.documentation_alert.vehicle_registration_number_or_vin',
                'Vehicle registration number/VIN',
              )}
              type={'string'}
            >
              <Input
                onChange={(input) => {
                  if (vinRegex.test(input)) {
                    setVehicleVinCode(input);
                    setVehicleRegNumber('');
                  } else {
                    setVehicleRegNumber(input);
                    setVehicleVinCode('');
                  }
                }}
                initialValue={vehicleRegNumber}
                placeholder={t(
                  'assistance.documentation_alert.vehicle_registration_number_or_vin.placeholder',
                  'Your vehicle registration number or VIN',
                )}
                bordered
              />
            </FormItem>
          </Flex>
          <Flex direction={'row'}>
            <FormItem name={'email'} label={t('common.email', 'E-mail address')} type={'email'} required>
              <Input
                onChange={(email) => {
                  setEmailData({ ...emailData, email });
                }}
                placeholder={t('common.email.address.placeholder', 'Type your e-mail address here')}
                bordered
              />
            </FormItem>
            <MarginBox ml={15} />
            <FormItem name={'subject'} label={t('common.email.object', 'Subject')} type={'string'}>
              <Input
                disabled
                onChange={(subject) => setEmailData({ ...emailData, subject })}
                placeholder={t('common.email.object.placeholder', `Please, enter your e-mail's subject`)}
                bordered
              />
            </FormItem>
            <MarginBox ml={15} />
            <FormItem
              name={'request'}
              label={t('assistance.documentation_alert.request', 'Request')}
              type={'string'}
              required
            >
              <Select
                onChange={(request) => {
                  setEmailData({
                    ...emailData,
                    request: request as DocumentationAlertMotrioRequest,
                  });
                }}
                options={requestOptions(t)}
                placeholder={t('assistance.documentation_alert.request.placeholder', 'Select a category')}
                bordered
                styleLess
              />
            </FormItem>
          </Flex>
          <FormItem
            name={'body'}
            label={t('assistance.documentation_alert.message', 'Message')}
            type={'string'}
            required
          >
            <TextArea
              onChange={(message) => {
                setEmailData({ ...emailData, message });
              }}
              style={{ height: '200px' }}
              initialValue={emailData.message}
              value={emailData.message}
              allowEnterNextLine
              disabled
            />
          </FormItem>
          <FormItem
            name={'body'}
            label={t('assistance.documentation_alert.additional_comments', 'Additional comments')}
            type={'string'}
            required
          >
            <TextArea
              onChange={(additionalComments) => {
                setAdditionalCommentsMessage(additionalComments);
                setEmailData({ ...emailData, additionalComments });
              }}
              style={{ height: '100px' }}
              initialValue={emailData.additionalComments}
              value={emailData.additionalComments}
              allowEnterNextLine
              disabled={isMessageUpdating}
            />
          </FormItem>
          <FormItem name={'sendCopy'} required={false} type={'boolean'}>
            <>
              <Flex>
                <Checkbox
                  checked={emailData?.sendCopy}
                  onChange={(sendCopy) => {
                    setEmailData({ ...emailData, sendCopy });
                  }}
                  label={t('common.email.request_copy', 'I would like to receive a copy of this e-mail.')}
                />
              </Flex>
              <MarginBox mt={15} />
              <Text type="sm" color="grey55">
                {t(
                  'assistance.documentation_alert.privacy_policy_note',
                  "By clicking “Send”, I confirm that I have read Rpartstore's  Privacy Policy.",
                )}
              </Text>
            </>
          </FormItem>
          <Flex justify={'flex-start'}>
            <WhiteButton onClick={handleCancel}>{t('common.action.cancel', 'Cancel')}</WhiteButton>
            <MarginBox ml={15} />
            <YellowButton disabled={sendDisable(emailData)} htmlType={'submit'}>
              {t('common.action.send', 'Send')}
            </YellowButton>
          </Flex>
        </Flex>
      </Form>
    </Flex>
  );
}
